import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { useServices } from "@/app/use-services";
import { errorMessage } from "@/lib/api/errors";
import { BackLink } from "@/ui/BackLink";
import { Button } from "@/ui/Button";
import { ErrorView } from "@/ui/ErrorView";
import { ErrorNote, Input, Readout } from "@/ui/Field";
import { Page } from "@/ui/Page";
import { Body, Display, Label, Tiny } from "@/ui/Text";
import { SITE, SOCIAL_QUERY_KEY } from "./keys";

const HANDLE_PATTERN = /^[a-z0-9_]{3,20}$/;

/** The handle and the page switch. Publishing asks once, because it is public. */
export function PublicPagePage() {
  const { api } = useServices();
  const client = useQueryClient();
  const social = useQuery({ queryKey: SOCIAL_QUERY_KEY, queryFn: () => api.fetchSocialProfile() });
  const [handle, setHandle] = useState("");
  const refresh = () => client.invalidateQueries({ queryKey: SOCIAL_QUERY_KEY });
  const claim = useMutation({ mutationFn: (h: string) => api.setUsername(h), onSuccess: () => void refresh() });
  const toggle = useMutation({ mutationFn: (on: boolean) => api.setProfilePublic(on), onSuccess: () => void refresh() });

  const claimed = social.data?.username;
  const isOn = social.data?.profile_public ?? false;
  const typed = handle.trim().toLowerCase();
  const valid = HANDLE_PATTERN.test(typed);

  function submit(event: FormEvent) {
    event.preventDefault();
    if (valid) claim.mutate(typed);
  }

  function togglePage() {
    if (!claimed) return;
    if (!isOn && !window.confirm("Publish your page?\n\nYour handle, your Sun, Moon and Rising, and today's line become visible to anyone with the address. Never your birth details.")) return;
    toggle.mutate(!isOn);
  }

  return (
    <Page>
      <BackLink to="/you" label="You" />
      <Label className="mt-3">Public page</Label>
      {social.isPending && <Body className="mt-3">Reading…</Body>}
      {social.isError && <ErrorView message={errorMessage(social.error)} onRetry={() => void social.refetch()} />}
      {social.data && (
        <>
          <Body className="mt-3">Your handle is what the share card prints and what every invitation points at. Claim it once; change it if you must.</Body>
          <form onSubmit={submit} className="mt-5">
            <Label>Handle</Label>
            {claimed && <Display size="md" as="p" className="mt-2">{SITE}/@{claimed}</Display>}
            <div className="mt-3">
              <Readout label="@">
                <Input display={false} autoCapitalize="none" autoCorrect="off" autoComplete="off" value={handle} onChange={(e) => setHandle(e.target.value)} placeholder={claimed ?? "yourname"} />
              </Readout>
            </div>
            <Tiny className="mt-2">Three to twenty characters. Lowercase letters, numbers and underscore.</Tiny>
            <ErrorNote message={claim.isError ? errorMessage(claim.error) : null} />
            <div className="mt-3">
              <Button type="submit" label={claimed ? "Change my handle" : "Claim my handle"} disabled={!valid} busy={claim.isPending} />
            </div>
          </form>
          <div className="mt-6">
            <Label>Page</Label>
            <Body className="mt-2">
              {!claimed
                ? "Claim a handle first. A page with no address is not a page."
                : isOn
                  ? "Your page is on. Anyone with the address can see your handle, your Sun, Moon and Rising, and today's line. Never your birth details."
                  : "Your page is off. The address goes nowhere, for everybody."}
            </Body>
            <ErrorNote message={toggle.isError ? errorMessage(toggle.error) : null} />
            {claimed && (
              <div className="mt-3">
                <Button label={isOn ? "Turn my page off" : "Turn my page on"} outlined onClick={togglePage} busy={toggle.isPending} />
              </div>
            )}
          </div>
        </>
      )}
    </Page>
  );
}
